import React, { useMemo } from 'react';
import styled from 'styled-components';
import Card from '@/components/Card';
import TextComponent from '@/components/TextComponent';
import { SettlementFilters } from '@/apis/settlement/interface';
import useSettlementQuery from '@/hooks/settlement/useSettlementQuery';
import { formatComma } from '@/utils/form';
import { ReactComponent as Question } from '@/assets/images/layout/question.svg';
import Tooltip from '@/components/Tooltip';
import theme from '@/styles/theme';

interface Props {
  filter: SettlementFilters;
  reimbursementSum: number;
  reimbursementSumWon: string;
}

const Settlements = (props: Props) => {
  const { filter, reimbursementSum, reimbursementSumWon } = props;

  const { settlementSummaryQuery, settlementDownloadMutation } =
    useSettlementQuery();

  const { data: settlement } = settlementSummaryQuery(filter);

  const onDownloadExcel = () => {
    settlementDownloadMutation.mutate(filter);
  };

  const totalSettlementAmount = useMemo(() => {
    if (!settlement) return 0;
    const totalPrice = settlement?.totalPrice ?? 0;
    const cardFee = settlement?.fee ?? 0;
    return formatComma(totalPrice - cardFee + reimbursementSum) + ' 원';
  }, [settlement, reimbursementSum]);

  return (
    <Wrapper>
      <Card>
        <Settlement>
          <LabelRow>
            <LabelWrapper>
              <Label>정산 예정 금액</Label>
              <a data-tip data-for="totalAmounts">
                <Question width={18} height={18} />
              </a>
              <Tooltip id="totalAmounts" place="top" type="info" effect="solid">
                <TextComponent color={'#fff'}>
                  [총 결제액 - 총 카드 수수료 + 총 변동 비용]이
                </TextComponent>
                <TextCenterComponent color={'#fff'}>
                  계산된 금액입니다.
                </TextCenterComponent>
              </Tooltip>
            </LabelWrapper>
            <ExcelDownload onClick={onDownloadExcel}>
              엑셀 다운로드
            </ExcelDownload>
          </LabelRow>
          <Total>
            <TotalPrice>{totalSettlementAmount}</TotalPrice>
            <TotalCount> (총 {formatComma(settlement?.count!)}건)</TotalCount>
          </Total>
        </Settlement>
      </Card>
      <Card>
        <DetailWrapper>
          <Detail>
            <DetailItem border>
              <DetailItemWrapper>
                <DetailItemLabel>총 결제액</DetailItemLabel>
                <a data-tip data-for="payments">
                  <Question width={18} height={18} />
                </a>
                <Tooltip id="payments" place="top" type="info" effect="solid">
                  <TextComponent color={'#fff'}>
                    총 환자 본인 부담금입니다.
                  </TextComponent>
                </Tooltip>
              </DetailItemWrapper>
              <DetailItemPrice>
                {formatComma(settlement?.totalPrice!)} 원
              </DetailItemPrice>
            </DetailItem>
            <DetailItem border>
              <DetailItemWrapper>
                <DetailItemLabel>총 카드 수수료</DetailItemLabel>
                <a data-tip data-for="cardfee">
                  <Question width={18} height={18} />
                </a>
                <Tooltip id="cardfee" place="top" type="info" effect="solid">
                  <TextComponent color={'#fff'}>
                    결제내역 총 카드 수수료입니다.
                  </TextComponent>
                  <TextComponent color={'#fff'}>
                    ※ 변동 비용 카드 수수료는 총 변동 비용에 포함
                  </TextComponent>
                </Tooltip>
              </DetailItemWrapper>
              <DetailItemPrice>
                {formatComma(settlement?.fee!)} 원
              </DetailItemPrice>
            </DetailItem>
            <DetailItem>
              <DetailItemWrapper>
                <DetailItemLabel>총 변동 비용</DetailItemLabel>
                <a data-tip data-for="variances">
                  <Question width={18} height={18} />
                </a>
                <Tooltip id="variances" place="top" type="info" effect="solid">
                  <TextComponent color={'#fff'}>
                    [변동금액 - 변동 카드 수수료]이
                  </TextComponent>
                  <TextCenterComponent color={'#fff'}>
                    계산된 금액입니다.
                  </TextCenterComponent>
                </Tooltip>
              </DetailItemWrapper>
              <DetailItemPrice>{reimbursementSumWon}</DetailItemPrice>
            </DetailItem>
          </Detail>
        </DetailWrapper>
      </Card>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;

  > * {
    &:nth-child(1) {
      flex: 1;
      margin-bottom: 12px;
    }
    &:nth-child(2) {
      flex: 1;
    }
  }
`;

const Settlement = styled.div`
  padding: 25px 35px;
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
`;
const Label = styled(TextComponent)`
  font-weight: 600;
  font-size: 16px;
`;

const ExcelDownload = styled(Label)`
  color: ${theme.DF_COLOR.GREEN500};
  border-bottom-width: 2px;
  border-bottom-color: #3fcc99;
  border-bottom-style: solid;

  &:hover {
    cursor: pointer;
    opacity: 0.7;
  }
`;
const Total = styled.div`
  display: flex;
  align-items: baseline;
`;
const TotalPrice = styled(TextComponent)`
  font-size: 30px;
  font-weight: 600;
  margin-right: 7px;
`;

const TotalCount = styled(TextComponent)`
  font-size: 16px;
  font-weight: 600;
`;

const DetailWrapper = styled.div`
  display: flex;
  padding: 20px 0;
  height: 100%;
`;

const Detail = styled.div`
  display: flex;
  width: 100%;
  text-align: center;
`;

const DetailItem = styled.div<{ border?: boolean }>`
  display: flex;
  width: 50%;
  flex-flow: column;
  justify-content: center;

  ${({ border }) =>
    border &&
    `  
      border-right-width: 1px;
      border-right-color: #d8d8d8;
      border-right-style: solid;
    `}
`;

const DetailItemWrapper = styled.div`
  display: flex;
  align-self: center;

  > * {
    &:nth-child(1) {
      margin-right: 5px;
    }
  }
`;

const LabelWrapper = styled(DetailItemWrapper)``;

const DetailItemLabel = styled(TextComponent)`
  font-size: 16px;
  font-color: #595959;
  font-weight: 600;
  margin-bottom: 10px;
`;
const DetailItemPrice = styled(TextComponent)`
  font-size: 22px;
`;

const TextCenterComponent = styled(TextComponent)`
  text-align: center;
`;

export default Settlements;
